/* Garden Fragrances: phone.js
   1. Auto-fit (all screens): trims the empty margin around each product photo
      so every bottle fills its tile the same way, without crossing the tile edge.
   2. Press-to-peek (phones only, under 990px): pressing and holding a product
      photo shows its second photo; letting go (or scrolling) switches straight back. */
(function () {
  'use strict';

  var mq = window.matchMedia('(max-width: 989px)');
  var MAX_ZOOM = 2.2;   // never blow up a photo more than this
  var SAMPLE = 240;     // width of the small copy we measure

  // Empty space kept around the bottle, per side. Desktop tiles have rounded
  // corners and more room, so they keep a little more.
  function margin() { return mq.matches ? 0.035 : 0.05; }

  /* ---------- 1. Auto-fit ------------------------------------------------ */

  function sampleUrl(img) {
    try {
      var u = new URL(img.currentSrc || img.src, window.location.href);
      u.searchParams.set('width', SAMPLE);
      u.searchParams.delete('height');
      u.searchParams.delete('crop');
      return u.toString();
    } catch (e) { return null; }
  }

  // Bounding box of the "real" product pixels, as fractions of the photo.
  function measure(probe) {
    var w = probe.naturalWidth, h = probe.naturalHeight;
    if (!w || !h) return null;
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(probe, 0, 0);
    var d = ctx.getImageData(0, 0, w, h).data; // throws if the CDN blocks it

    // Background = average of the four corners (handles white, cream or clear).
    var corners = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + w - 1) * 4];
    var br = 0, bg = 0, bb = 0, ba = 0;
    corners.forEach(function (i) { br += d[i]; bg += d[i + 1]; bb += d[i + 2]; ba += d[i + 3]; });
    br /= 4; bg /= 4; bb /= 4; ba /= 4;
    var clearBg = ba < 40;

    var x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = (y * w + x) * 4, a = d[i + 3], on;
        if (clearBg) on = a > 24;
        else on = a > 24 && (Math.abs(d[i] - br) + Math.abs(d[i + 1] - bg) + Math.abs(d[i + 2] - bb)) > 42;
        if (on) {
          if (x < x0) x0 = x; if (x > x1) x1 = x;
          if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < 0) return null;
    return { w: w, h: h, x0: x0 / w, y0: y0 / h, x1: (x1 + 1) / w, y1: (y1 + 1) / h };
  }

  function apply(img, box) {
    var tile = img.parentElement;
    var W = tile.clientWidth, H = tile.clientHeight;
    if (!W || !H) return;
    // Where the photo sits inside the tile with object-fit: contain.
    var k = Math.min(W / box.w, H / box.h);
    var dw = (box.w * k) / W, dh = (box.h * k) / H;     // as fractions of the tile
    var ox = (1 - dw) / 2, oy = (1 - dh) / 2;
    var bx0 = ox + dw * box.x0, bx1 = ox + dw * box.x1;
    var by0 = oy + dh * box.y0, by1 = oy + dh * box.y1;
    var room = 1 - 2 * margin();
    var z = Math.min(room / (bx1 - bx0), room / (by1 - by0), MAX_ZOOM);
    if (z < 1) z = 1;
    var tx = -z * ((bx0 + bx1) / 2 - 0.5) * 100;
    var ty = -z * ((by0 + by1) / 2 - 0.5) * 100;
    img.style.setProperty('transform-origin', '50% 50%', 'important');
    img.style.setProperty('transform', 'translate(' + tx.toFixed(2) + '%, ' + ty.toFixed(2) + '%) scale(' + z.toFixed(3) + ')', 'important');
    img.classList.add('is-fitted');
  }

  function fit(img) {
    if (img.__gfBox) { apply(img, img.__gfBox); return; }
    if (img.__gfBusy) return;
    var url = sampleUrl(img);
    if (!url) return;
    img.__gfBusy = true;
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.onload = function () {
      try {
        var box = measure(probe);
        if (box) { img.__gfBox = box; apply(img, box); }
      } catch (e) { /* CDN refused cross-origin reading: keep the plain fit */ }
      img.__gfBusy = false;
    };
    probe.onerror = function () { img.__gfBusy = false; };
    probe.src = url;
  }

  function fitAll(scope) {
    var imgs = (scope || document).querySelectorAll('.product-card__img:not(.placeholder)');
    Array.prototype.forEach.call(imgs, function (img) {
      if (img.complete && img.naturalWidth) fit(img);
      else img.addEventListener('load', function () { fit(img); }, { once: true });
    });
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { fitAll(); }, 150);
  }, { passive: true });

  // Cards added later (recommendations, theme editor) get fitted too.
  if ('MutationObserver' in window) {
    new MutationObserver(function (list) {
      list.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (n) {
          if (n.nodeType === 1) fitAll(n);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- 2. Press-to-peek ------------------------------------------ */

  /* Smooth hold-to-peek:
     - the second photo only fades in after a short, deliberate press, so
       scrolling past or quickly tapping never makes the photo flicker;
     - a quick tap still opens the product;
     - after a hold, letting go fades back and does NOT open the product. */
  var HOLD_MS = 160, MOVE_PX = 10;
  var pending = null, holdTimer = 0, peeking = null, startX = 0, startY = 0, swallowClick = null;

  function cancelHold() { clearTimeout(holdTimer); holdTimer = 0; pending = null; }
  function endPeek() {
    cancelHold();
    if (peeking) { peeking.classList.remove('is-peek'); peeking = null; }
  }

  document.addEventListener('touchstart', function (e) {
    if (!mq.matches || e.touches.length > 1) { endPeek(); return; }
    var media = e.target.closest && e.target.closest('.product-card__media');
    if (!media || !media.querySelector('.product-card__img--hover')) return;
    endPeek();
    pending = media.closest('.product-card');
    startX = e.touches[0].clientX; startY = e.touches[0].clientY;
    holdTimer = setTimeout(function () {
      if (!pending) return;
      peeking = pending; pending = null;
      peeking.classList.add('is-peek');
    }, HOLD_MS);
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!pending && !peeking) return;
    var t = e.touches[0];
    if (Math.abs(t.clientX - startX) > MOVE_PX || Math.abs(t.clientY - startY) > MOVE_PX) endPeek();
  }, { passive: true });

  document.addEventListener('touchend', function () {
    if (peeking) {
      // It was a hold, not a tap: don't open the product on release.
      swallowClick = peeking;
      setTimeout(function () { swallowClick = null; }, 450);
    }
    endPeek();
  }, { passive: true });
  document.addEventListener('touchcancel', endPeek, { passive: true });
  window.addEventListener('scroll', endPeek, { passive: true });
  window.addEventListener('pagehide', endPeek);

  document.addEventListener('click', function (e) {
    if (swallowClick && e.target.closest && e.target.closest('.product-card') === swallowClick &&
        e.target.closest('.product-card__media')) {
      e.preventDefault(); e.stopPropagation(); swallowClick = null;
    }
  }, true);

  // Load every second photo ahead of time so the first peek never shows a blank.
  function preloadHover(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('.product-card__img--hover'), function (img) {
      if (img.loading === 'lazy') img.loading = 'eager';
    });
  }
  // Hold-to-peek should not pop the iPhone image menu.
  document.addEventListener('contextmenu', function (e) {
    if (mq.matches && e.target.closest && e.target.closest('.product-card__media')) e.preventDefault();
  });

  /* ---------- Start ------------------------------------------------------ */
  function start() {
    fitAll();
    // After the page has settled, fetch the second photos in the background.
    var later = window.requestIdleCallback || function (fn) { return setTimeout(fn, 600); };
    later(function () { preloadHover(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', function (e) { fitAll(e.target); preloadHover(e.target); });
})();
